/**
 * Media Library Types
 *
 * Represents image assets uploaded to a project media library. Assets are
 * uploaded via FileUploadClient and enriched with AI-generated descriptions
 * and labels that users can edit later.
 */

import type { AnyFilter } from './views';

export enum MediaLibraryAssetTagKey {
    // Placement & identity
    ASSET_TYPE = 'assetType',
    SUBJECT = 'subject',
    BEST_USE_CASE = 'bestUseCase',
    LAYOUT_BEHAVIOR = 'layoutBehavior',
    PRIORITY_LEVEL = 'priorityLevel',

    // Visual & tone
    VISUAL_STYLE = 'visualStyle',
    TONE_MOOD = 'toneMood',
    COLOR_PROFILE = 'colorProfile',
    BACKGROUND_FIT = 'backgroundFit',

    // Composition & technical
    CROPPING_SAFETY = 'croppingSafety',
    ASPECT_RATIO = 'aspectRatio',
    ORIENTATION = 'orientation',
    HAS_TEXT_OVERLAY = 'hasTextOverlay',
    TRANSPARENCY_SUPPORT = 'transparencySupport',

    // Usage governance
    PAIRING_RULES = 'pairingRules',
    REUSE_GUIDANCE = 'reuseGuidance',
    REGION_CHANNEL_FIT = 'regionChannelFit',
    COMPLIANCE_FLAGS = 'complianceFlags',
    BRAND_SAFE = 'brandSafe',
    CAMPAIGN_TYPE = 'campaignType',
    SEASONALITY = 'seasonality',
    TARGET_AUDIENCE = 'targetAudience',

    // Freeform fallback
    FREEFORM_USAGE_NOTE = 'freeformUsageNote',
}

export type MediaLibraryAssetTags = Partial<
    Record<MediaLibraryAssetTagKey, string>
>;

export const mediaLibraryAssetTagKeys = Object.values(MediaLibraryAssetTagKey);

export enum MediaLibraryAssetStatus {
    READY = 'ready',
    PENDING = 'pending',
}

export type MediaLibraryAssetDimensions = {
    width: number;
    height: number;
};

export type MediaLibraryAsset = {
    uuid: string;
    projectUuid: string;
    organizationUuid: string;
    name: string;
    description: string | null;
    labels: string[];
    mimeType: string;
    sizeBytes: number;
    dimensions: MediaLibraryAssetDimensions | null;
    publicUrl: string;
    status: MediaLibraryAssetStatus;
    isHidden: boolean;
    createdAt: Date;
    updatedAt: Date;
    createdByUserUuid: string;
    tags: MediaLibraryAssetTags | null;
};

export type CreateMediaLibraryAsset = Omit<
    MediaLibraryAsset,
    'uuid' | 'createdAt' | 'updatedAt'
>;

export type UpdateMediaLibraryAsset = Partial<
    Pick<
        MediaLibraryAsset,
        'name' | 'description' | 'labels' | 'isHidden' | 'tags'
    >
>;

export enum MediaLibraryListSortColumn {
    NAME = 'name',
    SIZE = 'size',
    CREATED_AT = 'createdAt',
}

export type MediaLibraryListOptions = {
    currentPage: number;
    perPage: number;
    searchQuery?: string;
    /**
     * Label keywords to filter assets by. An asset matches if any of its labels
     * contain any of these keywords (case-insensitive partial match).
     */
    labels?: string[];
    sortBy?: MediaLibraryListSortColumn;
    sortOrder?: 'asc' | 'desc';
    filters?: AnyFilter[];
};

export type MediaLibraryFilters = {
    viewId?: string;
    filters?: string;
};

export enum MediaLibraryQueryParams {
    VIEW_ID = 'viewId',
    FILTERS = 'filters',
}

export type MediaLibraryAssetsList = {
    data: MediaLibraryAsset[];
    pagination: {
        currentPage: number;
        perPage: number;
        total: number;
    };
};

export type MediaLibraryAssetListResponse = {
    status: 'ok';
    results: MediaLibraryAssetsList;
};

export type MediaLibraryAssetResponse = {
    status: 'ok';
    results: MediaLibraryAsset;
};

export type MediaLibraryLabelsListResponse = {
    status: 'ok';
    results: string[];
};

export type MediaLibraryRegenerateDescriptionResponse = {
    status: 'ok';
    results: {
        description: string;
    };
};

export type MediaLibraryRegenerateTagsResponse = {
    status: 'ok';
    results: MediaLibraryAsset;
};
